import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  InlineField,
  KEEP_EDITING,
  NotPermitted,
  ReasonDialog,
  RecordSection,
  type IInlineControl,
  type InlineAnswer
} from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  IRankingAddress,
  IRankingList,
  IRankingLists,
  IRankingPlacement
} from '../../api/ranking-contracts';
import { isSendableSegment, rankingsApi } from '../../api/rankings-api';
import { LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { NOT_ARRIVED } from '../../components/field-controls';
import { RankingFlags } from '../../components/ranking-flags';
import { TableFrame } from '../../components/table-frame';
import { KEPT_UNDER_WORDS, notKeptHere, wordFor } from '../../figures';
import { internalMalformedHint } from '../../internal-field-values';
import { columnsOf } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { HUB_MEMORY_PAGE } from './hub-memory';
import { RankingHistory } from './rankings-history';
import { RankingTerritories } from './rankings-territories';
import type {
  IResearchPart,
  IResearchPartSection,
  IResearchWrites,
  IRevealRequest
} from './research';
import { isNotFound, OUT_OF_DATE, rereadsAfter, unanswered } from './said';

/**
 * The hub's **Rankings** — survey-api's placements of one member in one survey (the advisor record
 * page's S9, block F6; its plan's R1–R3 and R8), drawn inside that member's Veritant research part
 * through the part's slot (`IResearchPartSection`), the second block beside Asset & production and
 * before it in the slot's list — Salesforce's page holds its Rankings above its Asset & Production.
 *
 * **Which parts draw it is the wire's** (R1, the roadmap's B1, served): the block reads its survey's
 * list catalogue as the part mounts — once per survey per page load (`rankingsApi.lists`). A
 * survey whose catalogue is empty keeps no rankings, and draws **no fold at all** — not "no
 * rankings"; nothing is drawn while the catalogue is on its way; a catalogue that fails draws the
 * fold, with the failure said and "Read again" inside it, because a failure must never read as a
 * survey with no rankings. **The catalogue names the survey the placements are kept under**
 * (block F12's R2, over what survey-api `30d88f37` serves): a `CANADA_FR` part
 * reads its own code and is answered `CANADA`'s lists and placements with `keptUnder: "CANADA"`,
 * which it writes by and says under its title ("Kept under the Canada survey"). Rejected: the
 * mirror F6 kept, `RANKING_SURVEY_OF`, while the wire could not say it.
 *
 * **A fold read on its first opening** (`RecordSection`'s `lazy`), closed by default (U7), counting
 * nothing in its summary — F5's fold, for F5's reasons.
 *
 * **The tables draw what survey-api sent, and compute nothing but the grouping** (R3). The columns
 * of the page are `RANKING_LAYOUT`'s (U9, `columnsOf`): FA's lists in two tables side by side from
 * `lg` — stacked below it, the left first — every other survey's in one; each table in its own
 * `TableFrame`, so neither widens the page. **Rows are years, shared by both tables** so they line
 * up as the Lightning page's fields do, in the order the years first appear in the read — newest
 * first, as survey-api sends them; nothing here sorts. A head is the catalogue's label, "by
 * territory" beneath on a territorial list (the wire's `territorial`). A cell is its place as sent —
 * a 999 is 999, a place repeated across a list is drawn twice — and on a territorial list its
 * territory beneath, muted, as typed; a territorial placement the import found no territory for
 * draws nothing beneath. The placements are grouped by `(year, list)` and nothing else is derived:
 * the edition's name (Q1 ruled (a)), the origin, the Salesforce source and who set it are the
 * cell's history's and its accessible name's, never the face's (the roadmap's D11).
 *
 * **A cell is the kit's `InlineField` over a placement draft, and Save asks for a reason** (R5,
 * R11): the draft is `{place, territory}` as typed — a place box and, on a territorial list only
 * (the wire's `territorial`), a territory box; Enter in either saves, Tab moves between them.
 * Nothing is judged here: a place that reads as a number at all is sent as one — `0`, `-1` and
 * `1.5` go, for survey-api to refuse at `place` or `$.place` — and only text no number can be read
 * from (`abc`) is held, with the shape wanted; a territory is sent as typed, an empty one left out,
 * for survey-api to say a territorial list needs one. Save opens `ReasonDialog` (the reason
 * required, SR-507; `enterConfirms`, no length said — survey-api bounds none), whose Cancel answers
 * `KEEP_EDITING`; **an emptied place is a removal**, through the same dialog, and the territory
 * beside it is not sent. A draft equal to the placement drawn sends nothing and asks nothing.
 * **Every cell carries its history** (`RankingHistory`, R6), an empty one included — a removed
 * placement's cell is empty, and its history is where the removal is said — but R4's unsaved row,
 * which is no row of survey-api's yet. **A removed placement is the list's**: survey-api lists it
 * with a null place and territory (served by survey-api `30d88f37`, adopted by block F12's R4), so
 * **a year is a row because survey-api lists it** — one whose every placement was removed
 * included, on this visit and every later one, with its removals' histories. Rejected: the visit's
 * own set of emptied years F6 kept while the list left them out, which a later visit lost.
 *
 * **A write follows the hub's write rules** (R7, F5's word for word): a landing holds the answer —
 * the `PUT`'s placement, or an empty cell after the `DELETE` — over the tables as an overlay
 * dropped by **a placements read begun after the write landed** (counted here, never inferred from
 * a promise), then re-reads the hub through `reread` — a derived field moves with its placement —
 * which moves `written`, which re-reads the placements. A 400 is said beside the field with both
 * boxes kept and nothing re-read; a 404 says the page is out of date and re-reads; an answer that
 * never arrived is `NOT_ARRIVED`, and re-reads. Refusals are told apart by status alone —
 * survey-api's have no `type` that tells two apart (a 400's `problems[]`, a bodiless 404). A year
 * typed `.` or `..` cannot be put in an address at all, and is said here before any dialog. A read's
 * 404 is an unknown participant and nothing else (survey-api's D3): said so, and the hub re-read
 * once — never again until a read lands, since the hub's re-read moves `written`.
 *
 * **A new year is a row this page holds until one of its cells is saved** (R4, F5's new as-of
 * row): "Add a year" draws it at the top of both tables, "Not saved yet", every cell an empty
 * pencil; the first cell saved writes the year, and the re-read draws the row where survey-api
 * orders it. A blank box adds nothing, and a year already drawn is not added twice — its row's
 * first cell is focused instead. The year is not judged: `1999` is a row, and its first save is
 * survey-api's 400 at `year`, said beside the cell.
 *
 * **Re-read on `written`, never on the poll** (R7's read half): a placement moves on a write on this
 * page or an import — the hub's poll is about BrokerCheck. The newest read begun is kept
 * (`useAdminData`); a re-read that fails over drawn tables says so above them, never as the first
 * load's failure, which is said in words with "Read again".
 *
 * **A derived ranking field's press shows its cell** (R8, F5's link over the same `reveal`): the
 * fold opened the section nav's way, the tables awaited, the cell of the source's year and list
 * scrolled to — sideways too — and focused. Where the tables hold no such cell: "No placement in
 * <year> — the imported value is shown", which the part says beside the button; and so where the
 * survey keeps no rankings, since there are no tables to hold one. Nothing navigates.
 *
 * Rejected: a column per list in the catalogue's order (FA's eight are wider than the page — U9);
 * a row per placement, the list's own shape (the Lightning page reads a year across); and a hub
 * section of its own (U1, F5's Q1).
 */

/** The section's title — the heading, and the words a derived field's link names. Not "Rankings":
 *  FA's staff form holds a group of that name, whose fields are derived from these placements, so
 *  the slot draws the fold directly above it — two folds of one name, one over the other. */
const TITLE = 'Ranking placements';

/** Rankings, a block of Veritant research's parts (R1) — what `hubSections()` passes, first. Every
 *  survey is drawn here: whether it keeps rankings is its catalogue's to say, read by the block. */
export const rankingsPartSection: IResearchPartSection = {
  key: 'rankings',
  title: TITLE,
  sourceKind: 'ranking',
  mayHide: true,
  render: (part) => <RankingsSection part={part} />
};

type ListsRead =
  | { status: 'loading' }
  /** `catalogue` null for a code survey-api does not know (its 404). */
  | { status: 'loaded'; catalogue: Nullable<IRankingLists> }
  | { status: 'failed'; error: unknown };

/** A survey's catalogue, read once per page load by the reader; `retry` asks again after a
 *  failure, which the reader has forgotten. **A failure stays drawn while it is asked again**
 *  (gate 2): going back to `loading` drew no fold, so "Read again" took the fold, its own focus and
 *  the viewer's place away, and a second failure drew a fold afresh. */
function useRankingLists(surveyCode: string): { read: ListsRead; retry: () => void } {
  const [attempt, setAttempt] = React.useState(0);
  const [read, setRead] = React.useState<ListsRead>({ status: 'loading' });
  React.useEffect(() => {
    let live = true;
    setRead((was) => (was.status === 'failed' ? was : { status: 'loading' }));
    rankingsApi.lists(surveyCode).then(
      (catalogue) => {
        if (live) setRead({ status: 'loaded', catalogue });
      },
      (error: unknown) => {
        if (!(error instanceof ApiError)) console.error(error);
        if (live) setRead({ status: 'failed', error });
      }
    );
    return () => {
      live = false;
    };
  }, [surveyCode, attempt]);
  return { read, retry: () => setAttempt((count) => count + 1) };
}

function RankingsSection({ part }: { part: IResearchPart }) {
  const { member, surveyCode, surveyName, writes, reveal, revealed } = part;
  const { read, retry } = useRankingLists(surveyCode);
  const holder = React.useRef<HTMLDivElement>(null);
  const catalogue = read.status === 'loaded' ? read.catalogue : null;
  const empty = read.status === 'loaded' && (catalogue === null || catalogue.lists.length === 0);
  // A press opens the fold — and so mounts the tables, which read — before their own effect
  // answers it (F5's gate-2 fix): a fold shut again keeps its tables mounted (`lazy`), and a cell
  // inside a shut `<details>` can be neither scrolled to nor focused.
  React.useLayoutEffect(() => {
    const fold = holder.current?.querySelector('details');
    if (reveal !== null && fold && !fold.open) fold.open = true;
  }, [reveal, read.status]);
  // A press on a part whose survey keeps no rankings: there are no tables to show it in.
  const answer = React.useRef(revealed);
  answer.current = revealed;
  React.useEffect(() => {
    if (reveal !== null && empty) answer.current(reveal, missingPlacement(reveal));
  }, [reveal, empty]);

  // On a survey tab the nav names the section only while it is drawn (SR-474) — told once the
  // catalogue is read, never while it is out: the block remounts as the part's schema lands, and a
  // `false` said while it reads again would take the entry away for a moment.
  const drawn = read.status !== 'loading' && !empty;
  const shown = part.section?.shown;
  React.useEffect(() => {
    if (read.status !== 'loading') shown?.(drawn);
  }, [shown, drawn, read.status]);

  if (!drawn) return null;
  return (
    <div ref={holder}>
      <RecordSection
        page={HUB_MEMORY_PAGE}
        sectionKey={`rankings.${surveyCode}`}
        title={TITLE}
        level={part.section ? 2 : 4}
        anchor={part.section?.anchor}
        defaultOpen={false}
        filled={0}
        total={0}
        lazy
      >
        {catalogue !== null && catalogue.keptUnder !== surveyCode && (
          <p className="text-sm text-muted-foreground">
            Kept under the {wordFor(KEPT_UNDER_WORDS, catalogue.keptUnder)} survey
          </p>
        )}
        {read.status === 'failed' ? (
          <ListsFailure
            participantId={member.participantId}
            surveyCode={surveyCode}
            surveyName={surveyName}
            error={read.error}
            retry={retry}
            reveal={reveal}
            revealed={revealed}
          />
        ) : (
          // Loaded, and so a catalogue with lists: `empty` returned nothing above.
          catalogue !== null && (
            <RankingsTables
              participantId={member.participantId}
              surveyCode={surveyCode}
              keptUnder={catalogue.keptUnder}
              surveyName={surveyName}
              name={member.displayName}
              lists={catalogue.lists}
              writes={writes}
              reveal={reveal}
              revealed={revealed}
            />
          )
        )}
      </RecordSection>
    </div>
  );
}

/** A catalogue that could not be read, inside the fold (R1) — survey-api's refusal in its words,
 *  an answer that never arrived in the page's (the cause is in the console), and "Read again". A
 *  waiting press is shown the failure — scrolled to, as the tables' own failure is — and answered
 *  with nothing beside its button: the failure is said here. */
function ListsFailure({
  participantId,
  surveyCode,
  surveyName,
  error,
  retry,
  reveal,
  revealed
}: {
  participantId: string;
  surveyCode: string;
  surveyName: string;
  error: unknown;
  retry: () => void;
  reveal: Nullable<IRevealRequest>;
  revealed: (request: IRevealRequest, note: Nullable<string>) => void;
}) {
  const holder = React.useRef<HTMLDivElement>(null);
  const answer = React.useRef(revealed);
  answer.current = revealed;
  React.useEffect(() => {
    if (reveal === null) return;
    holder.current?.scrollIntoView({ block: 'center' });
    answer.current(reveal, null);
  }, [reveal]);
  const unread = `The ranking lists of ${surveyName} could not be read.`;
  return (
    <div
      ref={holder}
      data-rankings={`${participantId}:${surveyCode}`}
      className="grid justify-items-start gap-2"
    >
      <LoadError message={unanswered(error) ? unread : refusalMessage(error, unread)} />
      <Button type="button" size="sm" variant="outline" onClick={retry}>
        Read again
      </Button>
    </div>
  );
}

/** One placement's key among a read's — its year and list (the survey is the part's). */
function placementKey(year: number | string, list: string): string {
  return JSON.stringify([String(year), list]);
}

/** A write's answer, held over the tables until a read begun after it lands (R7). */
interface IHeldPlacement {
  placement: IRankingPlacement;
  /** How many placements reads had begun when the write landed. */
  after: number;
}

function RankingsTables({
  participantId,
  surveyCode,
  keptUnder,
  surveyName,
  name,
  lists,
  writes,
  reveal,
  revealed
}: {
  participantId: string;
  surveyCode: string;
  /** The survey the placements are kept under — the catalogue's, which a write is addressed by. */
  keptUnder: string;
  surveyName: string;
  name: string;
  lists: IRankingList[];
  writes: IResearchWrites;
  reveal: Nullable<IRevealRequest>;
  revealed: (request: IRevealRequest, note: Nullable<string>) => void;
}) {
  const { written, reread } = writes;
  // How many placements reads have begun — each read carries its number, so an overlay knows
  // whether the tables on screen were read after its write landed (R7).
  const readsBegun = React.useRef(0);
  // Whether a read's 404 has re-read the hub since a read last landed — once, or the re-read,
  // which moves `written`, would read the placements into the same 404 for as long as the hub
  // carries the member.
  const goneReread = React.useRef(false);
  const { data, error, reload } = useAdminData(async (signal) => {
    const read = ++readsBegun.current;
    try {
      const placements = await rankingsApi.placements(participantId, surveyCode, signal);
      goneReread.current = false;
      return { placements, read };
    } catch (readError) {
      if (isNotFound(readError) && !goneReread.current) {
        goneReread.current = true;
        void reread();
      }
      throw new Error(readFailure(readError, signal));
    }
  }, [participantId, surveyCode, written]);
  const [held, setHeld] = React.useState<ReadonlyMap<string, IHeldPlacement>>(() => new Map());
  const [newRow, setNewRow] = React.useState<Nullable<string>>(null);
  // Placements are written under `rankings.manage` (SR-228's split off `internal_fields.write`):
  // without it every pencil and "Add a year" is drawn off, under one sentence for the tables.
  const permitted = useCan()('rankings.manage');
  const reasonId = React.useId();

  const frame = React.useRef<HTMLDivElement>(null);
  const answer = React.useRef(revealed);
  answer.current = revealed;
  // A waiting press is answered once tables are drawn — or once the first read has failed, which
  // the section says itself.
  React.useEffect(() => {
    if (reveal === null || (data === null && error === null)) return;
    // R2: a field derived from another survey's placements has no cell here — said, nothing
    // focused.
    if (reveal.source.surveyCode !== keptUnder) {
      frame.current?.scrollIntoView({ block: 'center' });
      answer.current(reveal, notKeptHere(reveal.source.surveyCode, 'rankings'));
      return;
    }
    const { list, year } = reveal.source;
    const cell = list && year != null ? cellOf(frame.current, String(year), list) : null;
    if (!cell) {
      frame.current?.scrollIntoView({ block: 'center' });
      answer.current(reveal, data !== null ? missingPlacement(reveal) : null);
      return;
    }
    focusCell(cell);
    answer.current(reveal, null);
  }, [reveal, data, error, keptUnder]);

  // Holds the answer, then re-reads the hub — in that order, so the placements read `written`
  // starts is one begun after the overlay was taken. The answer is keyed by the year survey-api
  // stored, not the one typed: `02028` is its 2028, and the unsaved row typed so becomes that
  // year's, so the re-read that draws 2028 is the row the write landed in, never a second one.
  const land = (address: IRankingAddress, placement: IRankingPlacement) => {
    const after = readsBegun.current;
    const stored = String(placement.year);
    setHeld((cells) =>
      new Map(cells).set(placementKey(stored, placement.list), { placement, after })
    );
    setNewRow((row) => (row === String(address.year) ? stored : row));
    void reread();
  };

  // Once a read draws the unsaved row's year it is survey-api's row, and the page forgets it — so
  // a year whose last placement is removed later stays the row survey-api lists, never brought
  // back as "Not saved yet".
  React.useEffect(() => {
    if (newRow !== null && data?.placements.some((one) => String(one.year) === newRow)) {
      setNewRow(null);
    }
  }, [data, newRow]);

  // The years in the order the read first carries them — newest first, survey-api's order, a
  // year whose placements were all removed included.
  const years = [...new Set((data?.placements ?? []).map((placement) => String(placement.year)))];

  if (data === null) {
    return (
      <div ref={frame} data-rankings={`${participantId}:${surveyCode}`} className="grid gap-3">
        {error ? (
          <div className="grid justify-items-start gap-2">
            <LoadError message={error} />
            <Button type="button" size="sm" variant="outline" onClick={() => void reload()}>
              Read again
            </Button>
          </div>
        ) : (
          <TableSkeleton rows={2} />
        )}
      </div>
    );
  }

  const { placements: drawn, read } = data;
  const placements = new Map(
    drawn.map((placement) => [placementKey(placement.year, placement.list), placement])
  );
  // A held answer is drawn while the tables on screen were read no later than its write landed.
  const overlay = (year: string, list: string) => {
    const kept = held.get(placementKey(year, list));
    return kept !== undefined && read <= kept.after ? kept.placement : undefined;
  };
  // The unsaved row is drawn until a read draws its year (R4).
  const pending = newRow !== null && !years.includes(newRow) ? newRow : null;
  const rows = [
    ...(pending === null ? [] : [{ year: pending, unsaved: true }]),
    ...years.map((year) => ({ year, unsaved: false }))
  ];
  const { left, right } = columnsOf(keptUnder, lists);
  const sides = [
    { side: 'left', lists: left },
    ...(right.length === 0 ? [] : [{ side: 'right', lists: right }])
  ];

  return (
    <div ref={frame} data-rankings={`${participantId}:${surveyCode}`} className="grid gap-3">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the rankings as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {years.length === 0 && pending === null && (
        <p className="text-sm text-muted-foreground">No rankings yet for {surveyName}.</p>
      )}
      {!permitted && <NotPermitted id={reasonId} what="changing ranking placements" />}
      <AddYear
        disabledFor={permitted ? undefined : reasonId}
        drawn={(year) => years.includes(year) || year === pending}
        onAdd={setNewRow}
        onDrawn={(year) =>
          focusCell(cellOf(frame.current, year === pending ? NEW_ROW : year, left[0]?.list ?? ''))
        }
      />
      <div className={sides.length > 1 ? 'grid gap-3 lg:grid-cols-2' : 'grid gap-3'}>
        {sides.map(({ side, lists: columns }, index) => (
          <div key={side} className="min-w-0">
            <RankingsTable
              side={side}
              label={
                sides.length > 1
                  ? `${TITLE} of ${name}, ${index + 1} of ${sides.length}`
                  : `${TITLE} of ${name}`
              }
              lists={columns}
              rows={rows}
              saved={(year) => lists.some((list) => overlay(year, list.list) !== undefined)}
              cell={(year, list, unsaved) => (
                <RankingCellField
                  participantId={participantId}
                  address={{ surveyCode: keptUnder, list: list.list, year }}
                  list={list}
                  placement={
                    overlay(year, list.list) ??
                    placements.get(placementKey(year, list.list)) ??
                    null
                  }
                  name={name}
                  unsaved={unsaved}
                  notPermitted={permitted ? undefined : reasonId}
                  land={land}
                  reread={reread}
                />
              )}
            />
          </div>
        ))}
      </div>
      {lists.some((list) => list.territorial) && (
        <RankingTerritories
          participantId={participantId}
          keptUnder={keptUnder}
          name={name}
          lists={lists}
          permitted={permitted}
          notPermittedId={reasonId}
          written={written}
          reread={reread}
        />
      )}
    </div>
  );
}

/** R4's unsaved row's `data-ranking-row`. */
const NEW_ROW = 'new';

/** The cell of `year` and `list` inside the tables' frame — `year` is the row's hook. */
function cellOf(frame: Nullable<HTMLElement>, year: string, list: string): Nullable<HTMLElement> {
  return (
    frame?.querySelector<HTMLElement>(
      `[data-ranking-row="${CSS.escape(year)}"] [data-ranking-cell="${CSS.escape(list)}"]`
    ) ?? null
  );
}

/** Scrolls a cell into view — sideways too, since a table scrolls inside its frame and a row's own
 *  scroll never moves the frame — then focuses its pencil where it has one that is on, else the
 *  cell (`tabIndex={-1}`) — a disabled pencil takes no focus. */
function focusCell(cell: Nullable<HTMLElement>): void {
  cell?.scrollIntoView({ block: 'center', inline: 'center' });
  const pencil = cell?.querySelector<HTMLElement>('button[aria-label^="Edit "]:not(:disabled)');
  (pencil ?? cell)?.focus({ preventScroll: true });
}

/** What a press is told where the tables hold no cell for its source (R8) — or nothing where the
 *  source names no year. */
function missingPlacement(request: IRevealRequest): Nullable<string> {
  const { year } = request.source;
  return year == null ? null : `No placement in ${year} — the imported value is shown`;
}

function RankingsTable({
  side,
  label,
  lists,
  rows,
  saved,
  cell
}: {
  side: string;
  label: string;
  lists: IRankingList[];
  /** The years drawn, R4's unsaved one first. */
  rows: { year: string; unsaved: boolean }[];
  /** Whether a write in that year has landed — an unsaved row is "Not saved yet" until one has. */
  saved: (year: string) => boolean;
  cell: (year: string, list: IRankingList, unsaved: boolean) => React.ReactNode;
}) {
  return (
    <TableFrame>
      <Table aria-label={label} data-ranking-side={side}>
        <TableHeader>
          {/* Both tables' rows are one height, header and body alike, so a year lines up across
              them whatever wraps in one: three lines of a head, a place and its territory. */}
          <TableRow className="h-20">
            <TableHead className="align-bottom">Year</TableHead>
            {lists.map((list) => (
              <TableHead key={list.list} className="align-bottom">
                <ListHeader list={list} />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ year, unsaved }) => (
            // Keyed by its year, the unsaved row too: the re-read that draws its year keeps the
            // row — a neighbour's open draft and the saved cell's focus. `NEW_ROW` is the hook.
            <TableRow key={year} data-ranking-row={unsaved ? NEW_ROW : year} className="h-14">
              <TableCell className="align-top whitespace-nowrap">
                <span className="grid">
                  <span>{year}</span>
                  {unsaved && !saved(year) && (
                    <span className="text-xs text-muted-foreground">Not saved yet</span>
                  )}
                </span>
              </TableCell>
              {lists.map((list) => (
                <TableCell
                  key={list.list}
                  data-ranking-cell={list.list}
                  // Focused by a derived field's link (R8) where its pencil is not drawn.
                  tabIndex={-1}
                  className="min-w-36 align-top"
                >
                  {cell(year, list, unsaved)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

/** A column's head: the list's label, "by territory" beneath on a territorial list. */
function ListHeader({ list }: { list: IRankingList }) {
  return (
    <span className="grid">
      <span>{list.label}</span>
      {list.territorial && (
        <span className="text-xs font-normal text-muted-foreground">by territory</span>
      )}
    </span>
  );
}

/** One cell's face: the place as sent and, where it has one, the territory beneath — or, for an
 *  empty cell, its pencil alone, named "No placement" for assistive technology (R3). */
function PlacementValue({ placement }: { placement: Nullable<IRankingPlacement> }) {
  if (placement === null) return <span className="sr-only">No placement</span>;
  // A removed placement survey-api lists (`place: null`) with a territory: drawn as sent (R4).
  if (placement.place === null && placement.territory === null) {
    return <span className="sr-only">No placement</span>;
  }
  return (
    <span className="grid">
      {placement.place === null ? (
        <span className="sr-only">No placement</span>
      ) : (
        <span>{placement.place}</span>
      )}
      {placement.territory !== null && (
        <span data-territory className="text-xs text-muted-foreground">
          {placement.territory}
        </span>
      )}
      <RankingFlags flags={placement.flags} />
    </span>
  );
}

/** R4's "Add a year": a text box and "Add". A blank box adds nothing; a year the tables already
 *  draw is said and its row's first cell focused, never added twice. The year is not judged. */
function AddYear({
  disabledFor,
  drawn,
  onAdd,
  onDrawn
}: {
  /** The id of the `NotPermitted` sentence saying why the press is off; absent, it is on. */
  disabledFor?: string;
  drawn: (year: string) => boolean;
  onAdd: (year: string) => void;
  onDrawn: (year: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [year, setYear] = React.useState('');
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const inputId = React.useId();

  const close = () => {
    setOpen(false);
    setSaid(null);
    setYear('');
  };

  if (!open) {
    return (
      <div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabledFor !== undefined}
          aria-describedby={disabledFor}
          onClick={() => setOpen(true)}
        >
          Add a year
        </Button>
      </div>
    );
  }

  const add = () => {
    const typed = year.trim();
    if (typed === '') return;
    if (drawn(typed)) {
      setSaid(`${typed} is already a row.`);
      onDrawn(typed);
      return;
    }
    onAdd(typed);
    close();
  };

  return (
    <div className="grid justify-items-start gap-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1 text-sm">
          <label htmlFor={inputId} className="text-muted-foreground">
            New year
          </label>
          <Input
            id={inputId}
            inputMode="numeric"
            autoFocus
            className="w-28"
            value={year}
            onChange={(event) => {
              setYear(event.target.value);
              setSaid(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
          />
        </div>
        <Button type="button" size="sm" onClick={add}>
          Add
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={close}>
          Cancel
        </Button>
      </div>
      {said && (
        <p role="alert" className="text-sm text-destructive">
          {said}
        </p>
      )}
    </div>
  );
}

/** A cell's draft: the two boxes as typed. */
interface IPlacementDraft {
  place: string;
  territory: string;
}

const EMPTY_DRAFT: IPlacementDraft = { place: '', territory: '' };

/** Plain decimal text — a sign, digits (thousands grouped by commas, or not) and a fraction. */
const PLAIN_DECIMAL = /^-?(\d+|\d{1,3}(,\d{3})+)(\.\d+)?$/;

/** The place a box holds as the number sent — plain decimal text read as written, its commas
 *  dropped (`1.5`, `0` and `-1` go, for survey-api to refuse) — or null for any other text, which
 *  the field holds (R5): `Number()` would read `1e1` as 10 and `0x1F` as 31, and the respondent
 *  form's `readNumber` `12%` as 12, none of which is what was typed. */
function placeOf(text: string): Nullable<number> {
  const typed = text.trim();
  return PLAIN_DECIMAL.test(typed) ? Number(typed.replace(/,/g, '')) : null;
}

/** What the reason dialog is open for: a placement to set, or a removal. */
type Asked = { place: number; territory: string } | { remove: true };

/** How a reason dialog ended: cancelled, the write landed, or survey-api's refusal. */
type Outcome = { kind: 'kept' } | { kind: 'landed' } | { kind: 'refused'; error: unknown };

/**
 * One cell, read and edited in place (R5–R7, R11). Save and an emptied place each ask for a reason
 * first and settle once the dialog has gone: `null` when the write landed, `KEEP_EDITING` when the
 * dialog was cancelled, or the refusal in words beside the field, both boxes kept.
 */
function RankingCellField({
  participantId,
  address,
  list,
  placement,
  name,
  unsaved,
  notPermitted,
  land,
  reread
}: {
  participantId: string;
  address: IRankingAddress;
  list: IRankingList;
  /** The placement drawn — the overlay's or the read's — or null for an empty cell. */
  placement: Nullable<IRankingPlacement>;
  name: string;
  /** In R4's unsaved row — no history to read yet. */
  unsaved: boolean;
  /** The id of the sentence saying why the pencil is off (`InlineField`'s); absent, it is on. */
  notPermitted?: string;
  land: (address: IRankingAddress, placement: IRankingPlacement) => void;
  reread: () => Promise<boolean>;
}) {
  const year = String(address.year);
  const filled = placement !== null && placement.place !== null ? placement : null;
  // The edition's name where there is one (Q1 ruled (a)); an empty cell has no edition yet.
  const label = filled ? filled.name : `${list.label}, ${year}`;
  const [asked, setAsked] = React.useState<Nullable<Asked>>(null);
  const [open, setOpen] = React.useState(false);
  // The field's write waits on the dialog: it is settled with `outcome` once the dialog has gone.
  const settle = React.useRef<Nullable<() => void>>(null);
  const outcome = React.useRef<Outcome>({ kind: 'kept' });

  const ask = (question: Asked): Promise<InlineAnswer> => {
    // Refused before any dialog: such a year cannot be put in an address at all (`rankings-api`).
    if (!isSendableSegment(year)) return Promise.resolve(`${year} cannot be sent as a year.`);
    return new Promise((resolve) => {
      outcome.current = { kind: 'kept' };
      settle.current = () => {
        const told = outcome.current;
        if (told.kind === 'kept') resolve(KEEP_EDITING);
        else if (told.kind === 'landed') resolve(null);
        else resolve(refusalOf(told.error));
      };
      setAsked(question);
      setOpen(true);
    });
  };

  const confirm = async (reason: string): Promise<Nullable<string>> => {
    if (asked === null) return null;
    try {
      if ('remove' in asked) {
        await rankingsApi.remove(participantId, address, reason);
        land(address, removedPlacement(filled, address, list));
      } else {
        const body = { place: asked.place, territory: asked.territory, reason };
        land(address, await rankingsApi.set(participantId, address, body));
      }
      outcome.current = { kind: 'landed' };
    } catch (error) {
      // Not survey-api's answer: a dropped connection, or this side's own fault — said to staff as
      // an answer that did not arrive, and kept here for whoever debugs it.
      if (!(error instanceof ApiError)) console.error(error);
      if (rereadsAfter(error)) void reread();
      outcome.current = { kind: 'refused', error };
    }
    // Closes the dialog: what was said belongs beside the cell, with the draft kept.
    return null;
  };

  const onClosed = () => {
    const done = settle.current;
    settle.current = null;
    setAsked(null);
    done?.();
  };

  const removing = asked !== null && 'remove' in asked;

  return (
    <>
      <InlineField<IPlacementDraft>
        label={label}
        name={name}
        fieldKey={`ranking.${address.surveyCode}.${list.list}.${year}`}
        notPermitted={notPermitted}
        display={<PlacementValue placement={placement} />}
        value={
          filled ? { place: String(filled.place), territory: filled.territory ?? '' } : EMPTY_DRAFT
        }
        renderControl={(control) => <PlacementControl list={list} year={year} control={control} />}
        isEmpty={(draft) => draft.place.trim() === ''}
        onSave={async (draft) => {
          const place = placeOf(draft.place);
          if (place === null) return internalMalformedHint('int');
          // A national list offers no territory box, and sends none.
          const territory = list.territorial ? draft.territory : '';
          const unchanged =
            filled !== null && place === filled.place && territory === (filled.territory ?? '');
          return unchanged ? null : ask({ place, territory });
        }}
        onClear={() => ask({ remove: true })}
        actions={
          unsaved ? undefined : (
            <RankingHistory
              participantId={participantId}
              address={address}
              label={label}
              name={name}
              origin={filled?.origin ?? null}
            />
          )
        }
      />
      {/* Mounted for one write at a time, not once per cell of the tables. */}
      {asked && (
        <ReasonDialog
          open={open}
          onOpenChange={setOpen}
          onClosed={onClosed}
          title={removing ? 'Remove this placement' : 'Set this placement'}
          description={
            `${label} for ${name}. The system keeps the reason with the change, in the ` +
            'placement’s history.'
          }
          required
          enterConfirms
          confirmLabel={removing ? 'Remove it' : 'Save it'}
          onConfirm={confirm}
        />
      )}
    </>
  );
}

/** The two boxes of a cell's editor: the place, and on a territorial list the territory (R5). */
function PlacementControl({
  list,
  year,
  control
}: {
  list: IRankingList;
  year: string;
  control: IInlineControl<IPlacementDraft>;
}) {
  const { value, onChange, disabled } = control;
  return (
    <div className="grid gap-1.5">
      <Input
        aria-label={`Place, ${list.label}, ${year}`}
        inputMode="numeric"
        value={value.place}
        disabled={disabled}
        onChange={(event) => onChange({ ...value, place: event.target.value })}
      />
      {list.territorial && (
        <Input
          aria-label={`Territory, ${list.label}, ${year}`}
          value={value.territory}
          disabled={disabled}
          onChange={(event) => onChange({ ...value, territory: event.target.value })}
        />
      )}
    </div>
  );
}

/** The placement a `DELETE` leaves — survey-api keeps the row with no place — held until the
 *  re-read lands. Only its place is drawn on the tables' face, as nothing. */
function removedPlacement(
  was: Nullable<IRankingPlacement>,
  address: IRankingAddress,
  list: IRankingList
): IRankingPlacement {
  return {
    surveyCode: address.surveyCode,
    list: list.list,
    listLabel: list.label,
    year: Number(address.year),
    name: was?.name ?? `${address.year} ${list.label}`,
    origin: 'staff',
    setAt: new Date().toISOString(),
    actor: null,
    source: null,
    place: null,
    territory: null
  };
}

/** A refusal in words, by status (R7): an answer that never arrived is `NOT_ARRIVED`; a bodiless
 *  404 is the page out of date; anything else is `refusalMessage`'s — a 400's `problems[]` at
 *  `place`, `$.place`, `territory` or `year` in survey-api's own words. */
function refusalOf(error: unknown): string {
  if (unanswered(error)) return NOT_ARRIVED;
  if (isNotFound(error)) {
    return `This placement is no longer where the page put it. ${OUT_OF_DATE}`;
  }
  return refusalMessage(error, 'The placement could not be saved.');
}

/** A failed placements read in words (R7's read half). survey-api's 404 is bodiless: an unknown
 *  participant and nothing else (F12's R3) — so the page is out of date. An answer that never
 *  arrived is said plainly and kept in the console for whoever debugs it; an abort is no failure,
 *  and `useAdminData` drops it. Any other refusal is survey-api's words — a 400 `invalid-query` at
 *  `survey` among them, which the page cannot provoke: it reads only the surveys the hub's
 *  responses name. */
function readFailure(error: unknown, signal: AbortSignal): string {
  if (isNotFound(error)) {
    return 'The system holds no such participant any more — the page may be out of date.';
  }
  if (unanswered(error)) {
    if (!(error instanceof ApiError) && !signal.aborted) console.error(error);
    return 'The rankings could not be read.';
  }
  return refusalMessage(error, 'The rankings could not be read.');
}
